import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { petAPI } from '../services/api';
import PetAvatar from '../components/PetAvatar';
import LoadingSpinner from '../components/LoadingSpinner';
import { 
  ArrowLeftIcon,
  CalendarIcon,
  ScaleIcon,
  ClockIcon,
  HeartIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const PetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const { data: pet, isLoading, isError, error } = useQuery(
    ['pet', id],
    () => petAPI.getPet(id),
    {
      enabled: !!id,
      select: (response) => response.data
    }
  );

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <LoadingSpinner message="Loading pet details..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Pet Not Found</h1>
          <p className="text-gray-600 mb-6">
            {error?.response?.data?.error || 'Unable to load pet details'}
          </p>
          <button
            onClick={() => navigate('/app/pets')}
            className="bg-primary-500 text-white px-6 py-3 rounded-lg hover:bg-primary-600 transition-colors"
          >
            Back to Pets
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAgeDisplay = (ageInMonths) => {
    if (!ageInMonths) return 'Age not specified';
    
    const years = Math.floor(ageInMonths / 12);
    const months = ageInMonths % 12;
    
    if (years === 0) {
      return `${months} month${months !== 1 ? 's' : ''} old`;
    } else if (months === 0) {
      return `${years} year${years !== 1 ? 's' : ''} old`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''}, ${months} month${months !== 1 ? 's' : ''} old`;
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <button
          onClick={() => navigate('/app/pets')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Back to Pets</span>
        </button>
        
        <button
          onClick={() => navigate(`/app/pets/${id}/edit`)}
          className="flex items-center space-x-2 bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition-colors"
        >
          <PencilIcon className="h-4 w-4" />
          <span>Edit Pet</span>
        </button>
      </div>

      {/* Pet Profile Card */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-8">
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            <div className="flex-shrink-0">
              <PetAvatar petType={pet.pet_type} size="3xl" animated={true} />
            </div>
            
            <div className="text-center md:text-left text-white flex-1">
              <h1 className="text-4xl font-bold mb-2">{pet.name}</h1>
              <div className="flex flex-col md:flex-row md:items-center md:space-x-6 space-y-2 md:space-y-0">
                <div className="flex items-center justify-center md:justify-start space-x-2">
                  <HeartIcon className="h-5 w-5" />
                  <span className="text-lg">{pet.pet_type}</span>
                </div>
                {pet.breed && (
                  <div className="text-lg opacity-90">
                    {pet.breed}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Pet Details Grid */}
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Age */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-blue-100 p-2 rounded-lg">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Age</h3>
              </div>
              <p className="text-gray-600">{getAgeDisplay(pet.age)}</p>
            </div>

            {/* Weight */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-green-100 p-2 rounded-lg">
                  <ScaleIcon className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Weight</h3>
              </div>
              <p className="text-gray-600">
                {pet.weight ? `${pet.weight} kg` : 'Weight not specified'}
              </p>
            </div>

            {/* Member Since */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-purple-100 p-2 rounded-lg">
                  <CalendarIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Member Since</h3>
              </div>
              <p className="text-gray-600">{formatDate(pet.created_at)}</p>
            </div>

            {/* Pet Type Details */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center space-x-3 mb-3">
                <div className="bg-orange-100 p-2 rounded-lg">
                  <HeartIcon className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Type</h3>
              </div>
              <p className="text-gray-600">{pet.pet_type}</p>
              {pet.breed && (
                <p className="text-sm text-gray-500 mt-1">Breed: {pet.breed}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Information</h2>
        
        <div className="space-y-6">
          {/* Basic Info Summary */}
          <div className="bg-blue-50 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-blue-900 mb-4">Profile Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Full Name:</span>
                <span className="ml-2 text-blue-700">{pet.name}</span>
              </div>
              <div>
                <span className="font-medium text-blue-800">Species:</span>
                <span className="ml-2 text-blue-700">{pet.pet_type}</span>
              </div>
              {pet.breed && (
                <div>
                  <span className="font-medium text-blue-800">Breed:</span>
                  <span className="ml-2 text-blue-700">{pet.breed}</span>
                </div>
              )}
              {pet.age && (
                <div>
                  <span className="font-medium text-blue-800">Age:</span>
                  <span className="ml-2 text-blue-700">{pet.age} months</span>
                </div>
              )}
              {pet.weight && (
                <div>
                  <span className="font-medium text-blue-800">Weight:</span>
                  <span className="ml-2 text-blue-700">{pet.weight} kg</span>
                </div>
              )}
              <div>
                <span className="font-medium text-blue-800">Joined:</span>
                <span className="ml-2 text-blue-700">{formatDate(pet.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Training Readiness */}
          <div className="bg-green-50 rounded-xl p-6">
            <h3 className="text-xl font-semibold text-green-900 mb-4">Training Readiness</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-green-800">Profile complete and ready for training</span>
              </div>
              {pet.age && (
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-800">Age-appropriate exercises available</span>
                </div>
              )}
              {pet.weight && (
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-800">Weight-based recommendations enabled</span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => navigate(`/app/exercises?petId=${id}`)}
              className="flex-1 bg-primary-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
            >
              View Compatible Exercises
            </button>
            <button
              onClick={() => navigate(`/app/training/pet/${id}`)}
              className="flex-1 bg-secondary-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-secondary-600 transition-colors"
            >
              View Training History
            </button>
            <button
              onClick={() => navigate('/app/virtual-vet')}
              className="flex-1 bg-green-500 text-white py-3 px-6 rounded-xl font-semibold hover:bg-green-600 transition-colors"
            >
              Consult AI Vet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PetDetail;